import { Checkbox, Input, snackbar } from "@mobiscroll/react5";
import QRCode from "qrcode.react";
import { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import Loader from "./common/Loader";
import { fetchMFAPreference, getCurrentUser, setUpTOTP, updateMFAPreference, verifyTOTPSetup, } from "@aws-amplify/auth";
import greenCheck from "src/assets/green_check.svg";

export const MFASettings = () => {
  const [mfa, setMfa] = useState(null);
  const [loading, setLoading] = useState(false);
  const [codeTOTP, setCodeTOTP] = useState(null);
  const [answer, setAnswer] = useState("");
  const [authCode, setAuthCode] = useState("");
  const [isTOTPEnabled, setIsTOTPEnabled] = useState(false);
  const [isTOTPVerifying, setIsTOTPVerifying] = useState(false);

  useEffect(() => {
    fetchMFAPreference().then((data) => {
      setMfa(data);
      const enabledByObject = Array.isArray(data?.enabled) && data.enabled.includes("TOTP");
      const enabledByString = data === "SOFTWARE_TOKEN_MFA"; // in case mfa was set to a string elsewhere
      setIsTOTPEnabled(enabledByObject || enabledByString);
      setIsTOTPVerifying(false);
    });
  }, []);

  //change preferred MFA method
  const changePreferredMFA = (value) => {
    updateMFAPreference({
      sms: value === "SMS" ? "ENABLED" : "DISABLED",
      totp: value === "TOTP" ? "ENABLED" : "DISABLED",
    })
      .then((data) => {
        if (codeTOTP !== null) {
          setCodeTOTP(null);
        }
        snackbar({ message: "MFA (TOTP) option is successfully implemented.", color: "success" });
        if (value === "SMS") setMfa("SMS_MFA");
        else if (value === "TOTP") setMfa("SOFTWARE_TOKEN_MFA");
        else setMfa(value);
      })
      .catch((err) => {
        console.error(err);
        snackbar({ message: "Unable to switch MFA option", color: "danger" });
      });
  };

  //set up TOTP - show QR code and input field for TOTP code
  const _setUpTOTP = () => {
    setLoading(true);
    setUpTOTP()
      .then(async ({ getSetupUri, sharedSecret }) => {
        const { signInDetails: { loginId }, } = await getCurrentUser();
        setCodeTOTP(sharedSecret);
        const authCode = "otpauth://totp/DocG AI:" + loginId + "?secret=" + sharedSecret + "&issuer=DocG AI";
        setAuthCode(authCode);
        setIsTOTPVerifying(true);
      })
      .catch((err) => {
        console.error(err);
        snackbar({ message: "Unable to switch MFA option", color: "danger", });
        setIsTOTPVerifying(false);
      })
      .finally(() => {
        setLoading(false);
      })
  };

  //verify TOTP code
  const changeToTOTP = () => {
    if (answer === "") {
      snackbar({ message: "Code can't be empty", color: "danger" });
      return;
    }
    setLoading(true);

    verifyTOTPSetup({ code: answer })
      .then(() => {
        changePreferredMFA("TOTP");

        setIsTOTPEnabled(true);
        setIsTOTPVerifying(false);
        setCodeTOTP(null);
        setAnswer("");
      })
      .catch((err) => {
        console.error(err);
        snackbar({ message: "Unable to verify TOTP code", color: "danger" });
        setIsTOTPVerifying(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDisableTOTP = () => {
    setLoading(true);
    updateMFAPreference({ totp: "DISABLED", sms: "DISABLED" })
      .then(() => {
        setIsTOTPEnabled(false);
        setIsTOTPVerifying(false);
        setMfa("NOMFA");
        snackbar({ message: "TOTP method is removed successfully", color: "success" });
      })
      .catch((err) => {
        console.error(err);
        snackbar({ message: "Unable to remove TOTP method", color: "danger" });
      })
      .finally(() => {
        setLoading(false);
        if (codeTOTP !== null) {
          setCodeTOTP(null);
        }
      })
  }

  return (
    <Card className="docg-patient-form-wrapper docg-card h-standard mr-2">
      <div>
        <Card.Title className="d-flex align-items-center justify-content-between">
          MFA Settings
        </Card.Title>
        <Card.Body className="">
          <div className="custom-repeat-checkbox-cont d-flex justify-content-center gap-3 px-4 py-2">
            {(isTOTPEnabled || isTOTPVerifying) ? (
              <Checkbox
                value="mfa"
                label="No MFA"
                checked={!isTOTPEnabled && !isTOTPVerifying}
                onChange={() => {
                  handleDisableTOTP();
                }}
                style={{ cursor: "pointer", }}
              ></Checkbox>
            ) : (
              <div style={{ color: 'green', display: 'flex', alignItems: 'center', gap: '0.3rem', marginRight: '1.8rem' }}>
                <img src={greenCheck} alt="totp enabled" height={20} title="TOTP Disabled" />
                No MFA
              </div>
            )}

            <Checkbox
              value="mfa"
              label="SMS"
              disabled
              checked={mfa === "SMS_MFA"}
              onChange={() => {
                changePreferredMFA("SMS");
              }}
            ></Checkbox>

            {isTOTPEnabled ? (
              <div style={{ color: 'green', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <img src={greenCheck} alt="totp enabled" height={20} title="TOTP Enabled" />
                TOTP
              </div>
            ) : (
              <Checkbox
                className="totp-checkbox"
                value="tnc"
                label="TOTP"
                checked={isTOTPEnabled || isTOTPVerifying}
                onChange={() => {
                  _setUpTOTP();
                }}
                style={{ cursor: "pointer", }}
              ></Checkbox>
            )}
          </div>
        </Card.Body>

        {!loading && codeTOTP !== null && (
          <div style={{ textAlign: 'center' }}>
            <span style={{ fontWeight: '700', textDecoration: 'underline', fontSize: '1.3rem' }}>Setup TOTP</span>

            <div style={{ padding: '2rem', display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
              <div style={{ width: '60%' }}>
                <div>
                  <ul style={{ textAlign: 'left', lineHeight: '2' }}>
                    <li><b>Step 1:</b> First install an <b>Authenticator</b> app on your phone.</li>
                    <li><b>Step 2:</b> Scan the QR Code in your phone Authenticator App</li>
                    <li><b>Step 3:</b> Provide the 6 digit code below in the "Enter code" section</li>
                    <li><b>Step 4:</b> Click Verify, Now TOTP is enabled on the portal</li>
                  </ul>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-start', paddingLeft: '2.5rem', marginTop: '2rem' }}>
                  <div>
                    <Input
                      label="Enter code*"
                      labelStyle="floating"
                      type="Number"
                      inputMode="numeric"
                      maxLength={6}
                      style={{
                        width: '250px',
                        textAlign: 'center',
                        fontSize: '1.1rem',
                        padding: '0.5rem',
                      }}
                      placeholder="Enter 6-digit code"
                      onChange={(input) => setAnswer(input.target.value.replace(/\D/g, '').slice(0, 6))}
                      value={answer}
                    />
                  </div>
                  <div style={{ width: '30%', textAlign: 'center' }}>
                    <button className="docg-save-btn m-auto" style={{ width: '100px' }} onClick={() => { changeToTOTP(); }}>
                      Verify
                    </button>
                  </div>
                </div>
              </div>


              {authCode && (
                <div className="d-flex flex-column align-items-center justify-content-center">
                  <QRCode
                    id="qr-gen"
                    value={authCode}
                    up
                    size={216}
                    level={"H"}
                    includeMargin={true}
                  />
                </div>
              )}

            </div>
          </div>
        )}

        <Loader show={loading} />
      </div>
    </Card>
  );
};
